/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Mission Impact Auth API
 * OpenAPI spec version: v1
 */
import { useMutation } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryClient,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';

import type { ProblemDetails, RedeemTrialRequest } from '../missionImpactAuthAPI.schemas';

import { instance } from '../../mutator-auth';
import type { ErrorType } from '../../mutator-auth';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Redeems a product trial for the authenticated user's product registration, creating a new entitlement.
 * @summary Redeem trial for user
 */
export const redeemUserProductTrial = (
  productId: string,
  redeemTrialRequest: RedeemTrialRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<void>(
    {
      url: `/v1/products/${productId}/trials/redeem`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: redeemTrialRequest,
      signal,
    },
    options
  );
};

export const getRedeemUserProductTrialMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof redeemUserProductTrial>>,
    TError,
    { productId: string; data: RedeemTrialRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof redeemUserProductTrial>>,
  TError,
  { productId: string; data: RedeemTrialRequest },
  TContext
> => {
  const mutationKey = ['redeemUserProductTrial'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof redeemUserProductTrial>>,
    { productId: string; data: RedeemTrialRequest }
  > = (props) => {
    const { productId, data } = props ?? {};

    return redeemUserProductTrial(productId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RedeemUserProductTrialMutationResult = NonNullable<
  Awaited<ReturnType<typeof redeemUserProductTrial>>
>;
export type RedeemUserProductTrialMutationBody = RedeemTrialRequest;
export type RedeemUserProductTrialMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Redeem trial for user
 */
export const useRedeemUserProductTrial = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof redeemUserProductTrial>>,
      TError,
      { productId: string; data: RedeemTrialRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof redeemUserProductTrial>>,
  TError,
  { productId: string; data: RedeemTrialRequest },
  TContext
> => {
  const mutationOptions = getRedeemUserProductTrialMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Redeems a product trial for an organization's product registration, creating a new entitlement. Requires Administrator or Billing role in the organization.
 * @summary Redeem trial for organization
 */
export const redeemOrganizationProductTrial = (
  orgId: string,
  productId: string,
  redeemTrialRequest: RedeemTrialRequest,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<void>(
    {
      url: `/v1/orgs/${orgId}/products/${productId}/trials/redeem`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: redeemTrialRequest,
      signal,
    },
    options
  );
};

export const getRedeemOrganizationProductTrialMutationOptions = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof redeemOrganizationProductTrial>>,
    TError,
    { orgId: string; productId: string; data: RedeemTrialRequest },
    TContext
  >;
  request?: SecondParameter<typeof instance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof redeemOrganizationProductTrial>>,
  TError,
  { orgId: string; productId: string; data: RedeemTrialRequest },
  TContext
> => {
  const mutationKey = ['redeemOrganizationProductTrial'];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof redeemOrganizationProductTrial>>,
    { orgId: string; productId: string; data: RedeemTrialRequest }
  > = (props) => {
    const { orgId, productId, data } = props ?? {};

    return redeemOrganizationProductTrial(orgId, productId, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RedeemOrganizationProductTrialMutationResult = NonNullable<
  Awaited<ReturnType<typeof redeemOrganizationProductTrial>>
>;
export type RedeemOrganizationProductTrialMutationBody = RedeemTrialRequest;
export type RedeemOrganizationProductTrialMutationError = ErrorType<ProblemDetails>;

/**
 * @summary Redeem trial for organization
 */
export const useRedeemOrganizationProductTrial = <
  TError = ErrorType<ProblemDetails>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof redeemOrganizationProductTrial>>,
      TError,
      { orgId: string; productId: string; data: RedeemTrialRequest },
      TContext
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseMutationResult<
  Awaited<ReturnType<typeof redeemOrganizationProductTrial>>,
  TError,
  { orgId: string; productId: string; data: RedeemTrialRequest },
  TContext
> => {
  const mutationOptions = getRedeemOrganizationProductTrialMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
